import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import type { AmbiguityType } from '@/features/ambiguities/constants';
import type { GetAmbiguitiesResponse } from '@/features/ambiguities/types';
import { ANALYSIS_TERMINAL_STATUSES, AnalysisStatus } from '@/features/workspaces/constants';
import { resourceLatestVersionQuery } from '@/features/workspaces/queries';
import {
  isEmptySnapshotRow,
  parseStreamRow,
  queryStream,
  type StreamRowPayload,
} from '@/features/workspaces/stream';

export const AMBIGUITY_STATEMENTS_QUERY_KEY = 'ambiguityStatements' as const;

export const ambiguityStatementsQuery = (type: AmbiguityType, resourceId: string) => {
  const docType = DOCUMENT_TYPE[type];
  const queryKey = [AMBIGUITY_STATEMENTS_QUERY_KEY, type, resourceId] as const;
  const invalidate = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries(resourceLatestVersionQuery(resourceId));
  };
  return queryOptions({
    queryKey,
    queryFn: async () => {
      const response = await api.get<GetAmbiguitiesResponse>('v1/analysis/ambiguities', {
        params: { type: docType, resourceId },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });
      if (response.status === 404) return null;
      return response.data;
    },
    meta: {
      stream: queryStream<GetAmbiguitiesResponse | null, StreamRowPayload<AnalysisStatus>>({
        topic: 'ambiguity',
        type: docType,
        resourceId,
        versioned: true,
        isActive: (data) =>
          data?.status != null && !ANALYSIS_TERMINAL_STATUSES.includes(data.status),
        onStatusUpdate: (payload, queryClient) => {
          const current = queryClient.getQueryData<GetAmbiguitiesResponse | null>(queryKey);
          if (!('rows' in payload) && isEmptySnapshotRow(payload) && current === null) return;

          const row = parseStreamRow(payload);
          if (row === 'refetch') return invalidate(queryClient);
          if (!row) return;

          if (!current || current.ambiguityId !== row.rowId) return invalidate(queryClient);
          if (current.status === row.status && current.progress === row.progress) return;
          if (ANALYSIS_TERMINAL_STATUSES.includes(row.status)) return invalidate(queryClient);

          queryClient.setQueryData<GetAmbiguitiesResponse | null>(
            queryKey,
            (old) => old && { ...old, status: row.status, progress: row.progress },
          );
        },
      }),
    },
  });
};
